import React from 'react';
import {
  Controller,
  FieldPath,
  FieldValue,
  FieldValues,
  UseFormReturn,
} from 'react-hook-form';
import { Autocomplete, TextField, Chip } from '@mui/material';

type AutoCompleteInputProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  TValue extends FieldValue<TFieldValues>,
> = {
  form: UseFormReturn<TFieldValues>;
  name: TName;
  label: string;
  options: readonly TValue[];
  multiple?: boolean;
  getOptionLabel?: (option: TValue) => string;
  getOptionKey?: (option: TValue) => string;
  placeholder?: string;
};

export function AutoCompleteInput<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  TValue extends FieldValue<TFieldValues>,
>({
  form,
  name,
  label,
  options,
  multiple = false,
  getOptionLabel = (option: TValue) => option,
  getOptionKey = (option: TValue) => option,
  placeholder,
}: AutoCompleteInputProps<TFieldValues, TName, TValue>) {
  const { error } = form.getFieldState(name, form.formState);

  return (
    <Controller
      name={name}
      control={form.control}
      render={({ field }) => (
        <Autocomplete
          multiple={multiple}
          options={options}
          value={field.value || (multiple ? [] : null)}
          onChange={(_, newValue) => field.onChange(newValue)}
          renderTags={
            multiple
              ? (value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      variant="outlined"
                      label={getOptionLabel(option)}
                      {...getTagProps({ index })}
                      key={getOptionKey(option)}
                    />
                  ))
              : undefined
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label={label}
              placeholder={placeholder}
              error={!!error}
              helperText={error?.message}
              fullWidth
            />
          )}
          getOptionLabel={getOptionLabel}
        />
      )}
    />
  );
}
